import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  Box,
  Divider,
  useTheme,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Timeline,
  People,
  Star,
  Engineering,
  Security,
  Visibility
} from '@mui/icons-material';
import CountUp from 'react-countup';
import { useNavigate } from 'react-router-dom';

// Slider styled components (copied and adapted from Services.js)
const SliderSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  color: 'white',
  overflow: 'hidden',
  marginTop: '-90px',
  paddingTop: '90px',
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: '70px',
    marginTop: '-70px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60vh',
    paddingTop: '56px',
    marginTop: '-56px',
  },
}));

const SliderContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
});

const SlideImage = styled(motion.div)(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: isActive ? 1 : 0,
  zIndex: 1,
  filter: 'brightness(1.1)',
  transition: 'opacity 0.6s cubic-bezier(0.4,0,0.2,1)',
  pointerEvents: 'none',
}));

const SliderOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.2)',
  zIndex: 2,
});

const SliderDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '0.5rem',
  zIndex: 4,
});

const SliderDot = styled(Box)(({ isActive }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
}));

const SlideContent = styled(motion.div)(({ isActive, theme }) => ({
  opacity: isActive ? 1 : 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  pointerEvents: isActive ? 'auto' : 'none',
  zIndex: 3,
  paddingLeft: '6vw',
  color: 'white',
  [theme.breakpoints.down('md')]: {
    paddingLeft: '3vw',
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '2vw',
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: '#F8F9FA', // Light neutral background
  borderRadius: theme.spacing(2),
  padding: theme.spacing(6),
  marginBottom: theme.spacing(6),
  textAlign: 'center',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  height: '100%',
  background: 'linear-gradient(135deg, #fff, #f8f9fa)',
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const TeamMemberCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: theme.spacing(3),
  boxShadow: '0 8px 32px rgba(52,152,219,0.10)',
  background: 'linear-gradient(135deg, #f8faff 0%, #eaf7ff 100%)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
    boxShadow: '0 16px 48px 0 rgba(52,152,219,0.18), 0 8px 32px rgba(44,62,80,0.18)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '30px',
    background: 'linear-gradient(90deg, rgba(52,152,219,0.08) 0%, rgba(44,62,80,0.04) 100%)',
    filter: 'blur(2.5px)',
    zIndex: 1,
    opacity: 0.7,
  },
}));

const About = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);
  const navigate = useNavigate();

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();

  // Slider images for About page
  const sliderImages = [
    '/aboutSlider/1.jpg',
    '/aboutSlider/2.jpg',
    '/aboutSlider/3.jpg',
    '/aboutSlider/4.jpg',
  ];

  // Slider content (can be customized)
  const sliderContent = [
    {
      title: 'Building Dreams',
      description: 'Delivering exceptional construction services since 2008.',
    },
    {
      title: 'Expert Team',
      description: 'Our experienced professionals ensure quality and safety.',
    },
    {
      title: 'Innovative Solutions',
      description: 'We bring innovation and sustainability to every project.',
    },
    {
      title: 'Trusted Partner',
      description: 'Your reliable partner for all construction needs.',
    },
  ];

  // Auto-slide functionality
  useEffect(() => {
    if (!sliderImages || sliderImages.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearTimeout(timer);
  }, [currentSlide, sliderImages]);

  const stats = [
    { number: '500+', label: 'Projects Completed', icon: CheckCircle },
    { number: '15+', label: 'Years Experience', icon: Timeline },
    { number: '50+', label: 'Team Members', icon: People },
    { number: '98%', label: 'Client Satisfaction', icon: Star },
  ];

  const StatsSection = styled(Box)(({ theme }) => ({
    // width: '100%',
    // margin: 'auto',
    position: 'relative',
    padding: theme.spacing(8, 0),
    background: '#EAF7FF',
    color: '#2C3E50',
    padding: theme.spacing(8, 0),
    zIndex: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><defs><pattern id=\'grain\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'><circle cx=\'50\' cy=\'50\' r=\'1\' fill=\'%232C3E50\' opacity=\'0.05\'/></pattern></defs><rect width=\'100\' height=\'100\' fill=\'url(%23grain)\'/></svg>")',
      opacity: 0.1,
      pointerEvents: 'none',
    },
  }));

  const teamMembers = [
    {
      name: 'John Smith',
      role: 'CEO & Founder',
      experience: '20+ years in construction',
      avatar: '/teamPics/1.jpg'
    },
    {
      name: 'Sarah Johnson',
      role: 'Project Manager',
      experience: '15+ years in project management',
      avatar: '/teamPics/2.jpg'
    },
    {
      name: 'Mike Davis',
      role: 'Lead Engineer',
      experience: '12+ years in engineering',
      avatar: '/teamPics/3.jpg'
    },
  ];

  const values = [
    {
      title: 'Quality Workmanship',
      description: 'We never compromise on quality and use only the finest materials and techniques.',
      icon: Engineering
    },
    {
      title: 'Safety First',
      description: 'Safety is our top priority on every project, ensuring a secure work environment.',
      icon: Security
    },
    {
      title: 'Customer Satisfaction',
      description: 'We work closely with our clients to exceed their expectations on every project.',
      icon: Star
    }
  ];

  const CoreValueCard = styled(Card)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(4, 3),
    height: '100%',
    borderRadius: theme.spacing(3),
    boxShadow: '0 4px 24px rgba(52,152,219,0.08)',
    background: 'linear-gradient(135deg, #f8faff 0%, #eaf7ff 100%)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '24px',
      background: 'linear-gradient(90deg, rgba(52,152,219,0.06) 0%, rgba(44,62,80,0.03) 100%)',
      filter: 'blur(2.5px)',
      zIndex: 1,
      opacity: 0.5,
    },
  }));

  // Stats animation: only run once when section enters viewport
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [statDoneArr, setStatDoneArr] = useState([false, false, false, false]);
  const statsRef = React.useRef(null);
  useEffect(() => {
    let triggered = false;
    function checkAndAnimate() {
      if (statsRef.current) {
        const rect = statsRef.current.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView && !triggered) {
          setStatsAnimated(true);
          triggered = true;
        }
      }
    }
    checkAndAnimate();
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !triggered) {
          setStatsAnimated(true);
          triggered = true;
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    window.addEventListener('scroll', checkAndAnimate, { passive: true });
    window.addEventListener('resize', checkAndAnimate);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', checkAndAnimate);
      window.removeEventListener('resize', checkAndAnimate);
    };
  }, []);

  return (
    <>
      {/* Slider Section (copied from Services.js) */}
      <SliderSection>
        <SliderContainer>
          {sliderImages.map((img, idx) => (
            <SlideImage
              key={img}
              isActive={currentSlide === idx}
              style={{ backgroundImage: `url(${img})` }}
              animate={{ opacity: currentSlide === idx ? 1 : 0 }}
              transition={{ duration: 0.6 }}
            />
          ))}
          <SliderOverlay />
          {sliderContent.map((slide, idx) => (
            <SlideContent key={slide.title + '-content'} isActive={currentSlide === idx}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.2rem', sm: '1.7rem', md: '2.2rem', lg: '2.7rem' },
                    lineHeight: 1.1,
                    maxWidth: { xs: '90vw', sm: '70vw', md: '500px' },
                  }}
                >
                  {slide.title.split(' ').slice(0, 2).join(' ')}{' '}
                  {slide.title.split(' ').length > 2 && (
                    <span style={{ color: theme ? theme.palette.secondary.main : '#1976d2' }}>
                      {slide.title.split(' ').slice(2).join(' ')}
                    </span>
                  )}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 400,
                    opacity: 1,
                    maxWidth: '500px',
                    lineHeight: 1.6,
                  }}
                >
                  {slide.description}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<Visibility />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    mt: 3,
                    py: 1,
                    px: 2.5,
                    fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem', xl: '0.95rem' },
                    borderRadius: '50px',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    background: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Get In Touch
                </Button>
              </Box>
            </SlideContent>
          ))}
          <SliderDots>
            {sliderImages.map((_, idx) => (
              <SliderDot
                key={idx}
                isActive={currentSlide === idx}
                onClick={() => setCurrentSlide(idx)}
              />
            ))}
          </SliderDots>
        </SliderContainer>
      </SliderSection>

      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            fontWeight: 'bold',
            color: '#333333',
          }}
        >
          About Us
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' }}}>
          Building dreams, creating lasting value, and delivering exceptional construction
          services since 2008. We are your trusted partner for all construction needs.
        </Typography>
      </Box>

      {/* Stats Section */}
      <StatsSection ref={statsRef} sx={{ mb: 5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              let endValue = 0;
              let suffix = '';
              let statColor = '#2980b9';
              let iconBg = 'white';
              let iconShadow = '0 8px 32px rgba(52,152,219,0.18)';
              if (stat.label === 'Customer Support') {
                endValue = 24;
                suffix = '/7';
              } else if (stat.label === 'Client Satisfaction') {
                endValue = 98;
                suffix = '%';
              } else if (typeof stat.number === 'string') {
                const match = stat.number.match(/\d+/);
                endValue = match ? parseInt(match[0], 10) : 0;
                suffix = /\+/.test(stat.number) ? '+' : '';
              } else if (typeof stat.number === 'number') {
                endValue = stat.number;
              }
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      background: 'white',
                      borderRadius: 5,
                      boxShadow: '0 4px 24px rgba(44,62,80,0.07)',
                      p: 4,
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 2,
                      transition: 'box-shadow 0.3s, transform 0.3s',
                      transform: 'rotateY(0deg) scale(1)',
                      '&:hover': {
                        boxShadow: '0 12px 32px 0 rgba(52,152,219,0.18), 0 8px 32px rgba(44,62,80,0.18)',
                        transform: 'translateY(-10px) scale(1.04) rotateY(-8deg) skewY(-2deg)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        right: 18,
                        bottom: 10,
                        height: 18,
                        borderRadius: '50%',
                        background: 'radial-gradient(ellipse at center, rgba(44,62,80,0.10) 0%, rgba(44,62,80,0.02) 100%)',
                        filter: 'blur(2.5px)',
                        zIndex: 1,
                        opacity: 0.7,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        background: iconBg,
                        boxShadow: iconShadow,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `3px solid ${statColor}`,
                        transition: 'box-shadow 0.3s',
                      }}
                    >
                      <IconComponent sx={{ fontSize: 38, color: statColor }} />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        color: statColor,
                        fontSize: { xs: '2.1rem', md: '2.7rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                      }}
                    >
                      {statDoneArr[index] ? (
                        stat.label === 'Customer Support' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                            24<span style={{ fontSize: '1.7rem', fontWeight: 700, marginLeft: 2, color: statColor }}>/7</span>
                          </span>
                        ) : (
                          <>{endValue}{suffix}</>
                        )
                      ) : statsAnimated ? (
                        stat.label === 'Customer Support' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                            <CountUp end={24} duration={1.6} start={0} onEnd={() => {
                              setStatDoneArr(arr => {
                                const copy = [...arr];
                                copy[index] = true;
                                return copy;
                              });
                            }} />
                            <span style={{ fontSize: '1.7rem', fontWeight: 700, marginLeft: 2, color: statColor }}>/7</span>
                          </span>
                        ) : (
                          <CountUp
                            end={endValue}
                            duration={1.6}
                            suffix={suffix}
                            start={0}
                            onEnd={() => {
                              setStatDoneArr(arr => {
                                const copy = [...arr];
                                copy[index] = true;
                                return copy;
                              });
                            }}
                          />
                        )
                      ) : (
                        stat.label === 'Customer Support' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                            0<span style={{ fontSize: '1.7rem', fontWeight: 700, marginLeft: 2, color: statColor }}>/7</span>
                          </span>
                        ) : (
                          <>0{suffix}</>
                        )
                      )}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        opacity: 0.92,
                        fontWeight: 600,
                        color: '#2C3E50',
                        fontSize: '1.1rem',
                        letterSpacing: '0.01em',
                        mt: 1,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </StatsSection>

      {/* Company Story */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Founded in 2008, All In One Construction started as a small family business with
              a vision to provide honest, reliable, and high-quality construction services.
              Over the years, we have grown into one of the region's most trusted construction companies.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Our journey began with residential projects, and we have since expanded to include
              commercial, industrial, and infrastructure projects. Despite our growth, we have
              maintained our commitment to personalized service and attention to detail.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Today, we are proud to have completed over 500 projects, built lasting relationships
              with our clients, and contributed to the development of our community.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Our Mission
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              To deliver exceptional construction services that exceed our clients' expectations
              while maintaining the highest standards of quality, safety, and professionalism.
            </Typography>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
              Our Vision
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              To be the leading construction company in the region, known for innovation,
              sustainability, and building long-lasting relationships with our clients and community.
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6 }} />

        {/* Core Values */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, mb: 4 }}>
            Our Core Values
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <CoreValueCard>
                    <Box sx={{
                      width: 70,
                      height: 70,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #eaf7ff 0%, #f8faff 100%)',
                      boxShadow: '0 2px 12px rgba(52,152,219,0.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid #2980b9',
                    }}>
                      <IconComponent sx={{ fontSize: 38, color: '#2980b9' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, fontSize: '1.15rem', mt: 1 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: '1.02rem', mb: 1, px: 1 }}>
                      {value.description}
                    </Typography>
                  </CoreValueCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* Team Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, mb: 4 }}>
            Meet Our Leadership Team
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <TeamMemberCard>
                  <Box sx={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(52,152,219,0.18)',
                    border: '4px solid #2980b9',
                    background: 'linear-gradient(135deg, #eaf7ff 0%, #f8faff 100%)',
                  }}>
                    <img
                      src={member.avatar}
                      alt={member.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        display: 'block',
                      }}
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#2980b9', fontSize: '1.25rem', mt: 1 }}>
                    {member.name}
                  </Typography>
                  <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: 500, fontSize: '1.05rem' }}>
                    {member.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.98rem', mb: 1 }}>
                    {member.experience}
                  </Typography>
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                  }}>
                    {/* Social icons or badges can be added here for further modern look */}
                  </Box>
                </TeamMemberCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <HeroSection>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 3, letterSpacing: '-0.01em' }}>
            Why Choose Us?
          </Typography>
          <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <Security sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>Licensed and Insured</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>Fully licensed and insured for all types of construction projects.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <People sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>Experienced Team</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>Skilled professionals with years of industry experience.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <Engineering sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>Quality Materials</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>We use only premium materials for lasting results.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <Timeline sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>On-Time Delivery</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>Projects completed efficiently and on schedule.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <Star sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>Competitive Pricing</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>Transparent and fair pricing for every project.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(52,152,219,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 120,
              }}>
                <CheckCircle sx={{ color: '#2980b9', fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.1rem' }}>24/7 Support</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.98rem' }}>Always available to assist our clients, day or night.</Typography>
              </Box>
            </Grid>
          </Grid>
        </HeroSection>
      </Container>
    </>
  );
}

export default About;